import React, { useState } from 'react';
import {
  Building2,
  Plus,
  Search,
  Edit,
  CheckCircle,
  XCircle,
  Filter,
  Trash2,
  MapPin,
  Phone,
  Mail,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { useProveedores, useEliminarProveedor } from '../hooks/useProveedores';
import type { Proveedor } from '../services/proveedoresAPI';
import CrearProveedorModal from '../components/modals/CrearProveedorModal';
import EditarProveedorModal from '../components/modals/EditarProveedorModal';

// Categorías de proveedores desde categorias_generales
const CATEGORIAS_PROVEEDOR = [
  'Abarrotes',
  'Bebestibles',
  'Combustible',
  'Frutas y Verduras',
  'Gas',
  'otros',
  'Packing',
  'Proteínas',
  'Supermercado',
  'Vehículos',
];

// Componente principal de Proveedores
const Proveedores: React.FC = () => {
  const [filtros, setFiltros] = useState({
    busqueda: '',
    categoria: '',
    activo: '',
  });

  // Estados para paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const [elementosPorPagina] = useState(10);

  // Estados para modales
  const [mostrarModalCrear, setMostrarModalCrear] = useState(false);
  const [mostrarModalEditar, setMostrarModalEditar] = useState(false);
  const [proveedorAEditar, setProveedorAEditar] = useState<Proveedor | null>(
    null
  );

  // Hook para manejar proveedores
  const {
    data: proveedoresData,
    isLoading: loading,
    error,
    refetch,
  } = useProveedores({
    search: filtros.busqueda || undefined,
    categoria: filtros.categoria || undefined,
    activo: filtros.activo ? filtros.activo === 'true' : undefined,
    page: paginaActual,
    limit: elementosPorPagina,
  });

  // Hook para eliminar proveedor
  const eliminarProveedorMutation = useEliminarProveedor();

  const proveedores = proveedoresData?.data || [];
  const pagination = proveedoresData?.pagination;

  // Total de páginas desde el backend
  const totalPaginas = pagination?.totalPages || 1;
  const totalProveedores = pagination?.total || proveedores.length;

  // Ya no necesitamos filtrado local ni paginación local
  // El backend se encarga de todo
  const proveedoresPaginados = proveedores;

  // Handlers
  const handleFiltroChange = (key: string, value: string) => {
    setFiltros((prev) => ({ ...prev, [key]: value }));
    setPaginaActual(1); // Resetear a la primera página
  };

  const handleCrearProveedor = () => {
    setMostrarModalCrear(true);
  };

  const handleEditarProveedor = (proveedor: Proveedor) => {
    setProveedorAEditar(proveedor);
    setMostrarModalEditar(true);
  };

  const handleEliminarProveedor = async (proveedor: Proveedor) => {
    if (
      window.confirm(
        `¿Está seguro de que desea eliminar el proveedor "${proveedor.nombre}"?`
      )
    ) {
      try {
        await eliminarProveedorMutation.mutateAsync(proveedor.id);
      } catch (error) {
        console.error('Error al eliminar proveedor:', error);
      }
    }
  };

  // Función para obtener el color de la categoría
  const getCategoriaColor = (categoria: string) => {
    const colores: { [key: string]: string } = {
      supermercado:
        'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
      proteinas: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
      abarrotes:
        'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400',
      packing:
        'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
      gas: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-400',
      combustible:
        'bg-slate-100 text-slate-800 dark:bg-slate-900/20 dark:text-slate-400',
      vehiculos:
        'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400',
      frutas_verduras:
        'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      bebestibles:
        'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
      otros: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400',
    };
    return colores[categoria] || colores.otros;
  };

  // Vista principal: Lista de proveedores
  return (
    <div className="space-y-6">
      <PageHeader
        title="Gestión de Proveedores"
        helpSeccion="proveedores"
        subtitle="Administre los proveedores de su empresa"
      />

      <div className="mx-6 lg:mx-8 space-y-6">
        {/* Filtros y acciones */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
            {/* Filtros */}
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              {/* Búsqueda */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, RUT o email..."
                  value={filtros.busqueda}
                  onChange={(e) =>
                    handleFiltroChange('busqueda', e.target.value)
                  }
                  className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-80"
                />
              </div>

              {/* Filtro por categoría */}
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <select
                  value={filtros.categoria}
                  onChange={(e) =>
                    handleFiltroChange('categoria', e.target.value)
                  }
                  className="pl-10 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todas las categorías</option>
                  {CATEGORIAS_PROVEEDOR.map((categoria) => (
                    <option key={categoria} value={categoria}>
                      {categoria}
                    </option>
                  ))}
                </select>
              </div>

              {/* Filtro por estado */}
              <select
                value={filtros.activo}
                onChange={(e) => handleFiltroChange('activo', e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Todos los estados</option>
                <option value="true">Activos</option>
                <option value="false">Inactivos</option>
              </select>
            </div>

            {/* Botón crear */}
            <button
              onClick={handleCrearProveedor}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Nuevo Proveedor</span>
            </button>
          </div>
        </div>

        {/* Lista de proveedores */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-500 dark:text-gray-400">
                Cargando proveedores...
              </p>
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 dark:text-red-400">
                Error al cargar proveedores
              </p>
              <button
                onClick={() => refetch()}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Reintentar
              </button>
            </div>
          ) : proveedoresPaginados.length === 0 ? (
            <div className="p-8 text-center">
              <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                No se encontraron proveedores
              </p>
            </div>
          ) : (
            <>
              {/* Tabla */}
              <div className="overflow-hidden rounded-lg">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Proveedor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Contacto
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Categoría
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {proveedoresPaginados.map((proveedor) => (
                      <tr
                        key={proveedor.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        {/* Información del proveedor */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {proveedor.nombre}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              RUT: {proveedor.rut}
                            </div>
                          </div>
                        </td>

                        {/* Contacto */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-1">
                            {proveedor.telefono && (
                              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                <Phone className="h-3 w-3 mr-1" />
                                {proveedor.telefono}
                              </div>
                            )}
                            {proveedor.email && (
                              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                <Mail className="h-3 w-3 mr-1" />
                                {proveedor.email}
                              </div>
                            )}
                            {proveedor.direccion && (
                              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                <MapPin className="h-3 w-3 mr-1" />
                                {proveedor.direccion}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Categoría */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoriaColor(
                              proveedor.categoria
                            )}`}
                          >
                            {proveedor.categoria.charAt(0).toUpperCase() +
                              proveedor.categoria.slice(1)}
                          </span>
                        </td>

                        {/* Estado */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              proveedor.activo
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                            }`}
                          >
                            {proveedor.activo ? (
                              <CheckCircle className="h-3 w-3 mr-1" />
                            ) : (
                              <XCircle className="h-3 w-3 mr-1" />
                            )}
                            {proveedor.activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>

                        {/* Acciones */}
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditarProveedor(proveedor)}
                              className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                              title="Editar"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEliminarProveedor(proveedor)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              title="Eliminar"
                              disabled={eliminarProveedorMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Paginación mejorada */}
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Mostrando {(paginaActual - 1) * elementosPorPagina + 1} al{' '}
                    {Math.min(
                      paginaActual * elementosPorPagina,
                      totalProveedores
                    )}{' '}
                    de {totalProveedores} proveedores
                  </div>

                  <div className="flex items-center space-x-2">
                    {/* Botón página anterior */}
                    <button
                      onClick={() => setPaginaActual(paginaActual - 1)}
                      disabled={paginaActual === 1}
                      className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Anterior
                    </button>

                    {/* Números de página */}
                    <div className="flex items-center space-x-1">
                      {Array.from(
                        { length: Math.min(5, totalPaginas) },
                        (_, i) => {
                          let pageNum;
                          if (totalPaginas <= 5) {
                            pageNum = i + 1;
                          } else if (paginaActual <= 3) {
                            pageNum = i + 1;
                          } else if (paginaActual >= totalPaginas - 2) {
                            pageNum = totalPaginas - 4 + i;
                          } else {
                            pageNum = paginaActual - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => setPaginaActual(pageNum)}
                              className={`px-3 py-1 text-sm border rounded-md ${
                                pageNum === paginaActual
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        }
                      )}
                    </div>

                    {/* Botón página siguiente */}
                    <button
                      onClick={() => setPaginaActual(paginaActual + 1)}
                      disabled={paginaActual === totalPaginas}
                      className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal para crear proveedor */}
      <CrearProveedorModal
        isOpen={mostrarModalCrear}
        onClose={() => setMostrarModalCrear(false)}
        onSuccess={() => {
          setMostrarModalCrear(false);
          refetch(); // Recargar la lista de proveedores
        }}
      />

      {/* Modal para editar proveedor */}
      <EditarProveedorModal
        isOpen={mostrarModalEditar}
        onClose={() => {
          setMostrarModalEditar(false);
          setProveedorAEditar(null);
        }}
        onSuccess={() => {
          setMostrarModalEditar(false);
          setProveedorAEditar(null);
          refetch(); // Recargar la lista de proveedores
        }}
        proveedor={proveedorAEditar}
      />
    </div>
  );
};

export default Proveedores;
